import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { PDFDocument } from 'pdf-lib';
import axios from 'axios';
import { toast } from 'react-toastify';
import { buildArchiveFileName } from './naming';

  const testingBase = process.env.REACT_APP_TESTING_BASE;
  const uploadFiles = process.env.REACT_APP_UPLOADED;
  const prodBase = process.env.REACT_APP_API_BASE;
  const uploadFilesToOD = process.env.REACT_APP_UPLOAD_FILES;


export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

// buildTargetFileName used to live here and produced a SECOND, incompatible
// filename format: <Vendor>_<Invoice>_<YYYYMMDD>_V<Voucher>.pdf. The backend
// router only recognises V<Voucher>_<Vendor>_<Invoice>_<MM.DD.YYYY>.pdf, so
// anything built the old way was silently dropped into Uploaded/ instead of
// Invoices/{year}/, and VoucherSearch could not parse it either.
//
// There is now one builder, in lib/naming.js, mirrored on the backend.
// Re-exported here so existing imports keep working.
const buildTargetFileName = buildArchiveFileName;

// Given total pages and a 1-based selectedPages array, return 0-based complement ranges [start,end]
const complementRanges = (totalPages, selectedPages = []) => {
  const selectedSet = new Set(selectedPages.map((n) => n - 1));
  const ranges = [];
  let start = null;
  for (let i = 0; i < totalPages; i++) {
    if (!selectedSet.has(i)) {
      if (start === null) start = i;
    } else if (start !== null) {
      ranges.push([start, i - 1]);
      start = null;
    }
  }
  if (start !== null) ranges.push([start, totalPages - 1]);
  return ranges; // inclusive 0-based
};

async function makePdfSubset(fileOrBlob, zeroBasedPageIndices) {
  const srcBytes = await fileOrBlob.arrayBuffer();
  const srcDoc = await PDFDocument.load(srcBytes);
  const outDoc = await PDFDocument.create();
  const copied = await outDoc.copyPages(srcDoc, zeroBasedPageIndices);
  copied.forEach((p) => outDoc.addPage(p));
  const outBytes = await outDoc.save();
  return new Blob([outBytes], { type: 'application/pdf' });
}

async function getTotalPages(fileOrBlob) {
  const srcBytes = await fileOrBlob.arrayBuffer();
  const doc = await PDFDocument.load(srcBytes);
  return doc.getPageCount();
}

const refreshFileUrl = async (fileId) => {
  try {
    //const response = await axios.get(`${testingBase}${uploadFiles}`, {
    const response = await axios.get(`${prodBase}${uploadFiles}`, {
      params: { fileId }
    });
    
    if (response.data?.downloadUrl) {
      return response.data.downloadUrl;
    }
    throw new Error('No download URL in response');
  } catch (error) {
    console.error('Failed to refresh file URL:', error);
    throw error;
  }
};

const fetchWithRetry = async (url, fileId) => {
  try {
    const response = await fetch(url);
    
    if (!response.ok && response.status === 401) {
      // Token expired - get fresh URL and retry
      console.log('URL expired, fetching fresh URL...');
      const freshUrl = await refreshFileUrl(fileId);
      const retryResponse = await fetch(freshUrl);
      
      if (!retryResponse.ok) {
        throw new Error(`Retry failed: HTTP ${retryResponse.status}`);
      }
      
      return retryResponse;
    }
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    return response;
  } catch (error) {
    // On network errors, try refreshing URL once
    if (error.message.includes('Failed to fetch') && fileId) {
      console.log('Network error, trying fresh URL...');
      const freshUrl = await refreshFileUrl(fileId);
      return fetch(freshUrl);
    }
    throw error;
  }
};

const uploadPartFilesToOneDrive = async (partFiles, toast) => {
  console.log('Uploading PART files to OneDrive:', partFiles);
  
  const uploadPromises = partFiles.map(async (partFileData) => {
    const formData = new FormData();
    formData.append('files', partFileData.file);

    try {
      //const response = await axios.post(`${testingBase}${uploadFilesToOD}`, formData, {
      const response = await axios.post(`${prodBase}${uploadFilesToOD}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      console.log(`Uploaded ${partFileData.file.name} to OneDrive:`, response.data);
      
      return {
        success: true,
        fileName: partFileData.file.name,
        originalFileId: partFileData.originalFileId,
        response: response.data
      };
    } catch (error) {
      console.error(`Failed to upload ${partFileData.file.name}:`, error);
      return {
        success: false,
        fileName: partFileData.file.name,
        error: error.message
      };
    }
  });

  const results = await Promise.all(uploadPromises);
  
  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;
  
  if (successCount > 0) {
    toast(`${successCount} PART file(s) uploaded to OneDrive`, { autoClose: 3000, className: 'success-toast', closeOnClick: true });
  }
  if (failCount > 0) {
    toast(`${failCount} PART file(s) failed to upload`, { autoClose: 3000, className: 'error-toast', closeOnClick: true });
  }

  return results;
};


function confirmToast({
  title = 'Are you sure?',
  message = '',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  position = 'top-center',
  className = 'confirm-toast',   // style it via CSS/Tailwind
  autoClose = false,             // keep it open until a choice is made
} = {}) {
  return new Promise((resolve) => {
    let settled = false;
    const id = toast((t) => (
      <div className="flex items-start gap-3 p-3">
        <div className="flex-1">
          <div className="font-semibold">{title}</div>
          {message ? <p className="text-sm opacity-80 mt-0.5">{message}</p> : null}
          <div className="mt-3 flex gap-2">
            <button
              className="px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700"
              onClick={() => {
                settled = true;
                resolve(true);
                toast.dismiss(t.id);
              }}
            >
              {confirmText}
            </button>
            <button
              className="px-3 py-1.5 rounded-md bg-gray-300 border hover:bg-black/5"
              onClick={() => {
                settled = true;
                resolve(false);
                toast.dismiss(t.id);
              }}
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    ), {
      autoClose,                   // false = manual close only
      closeOnClick: false,
      draggable: false,
      icon: false,
      className,
      onClose: () => { if (!settled) resolve(false); }, // X or timeout => cancel
    });
  });
}




export { buildTargetFileName, getTotalPages, complementRanges, makePdfSubset, refreshFileUrl, fetchWithRetry, uploadPartFilesToOneDrive, confirmToast };

